/*
 * KpiTile: one number with its label. Not a Figma component; the retail views' KPI cards (their
 * extension's components/vendors/KpiCards.tsx) and the usage page's tiles, made one.
 * Tile: 1px --border-default, radius 8, padding 16 (12 on a phone), column gap 4, --surface-default.
 * Label: 400 14/20 --text-weak, with a 6px status dot when `tone` is set.
 * Value: 600 22/28, --text-danger / --text-warning for those tones, else --text-default.
 * Sub-line: 400 14/20 --text-weak. While `value` is undefined (loading) the value is a skeleton.
 */
import type { ReactNode } from 'react'
import type { Tone } from './Badge'
import { cx } from './cx'
import { Skeleton } from './Skeleton'

const DOT: Record<Tone, string> = {
    success: 'bg-[var(--status-success)]',
    danger: 'bg-[var(--status-danger)]',
    warning: 'bg-[var(--status-warning)]',
    info: 'bg-[var(--status-info)]',
    neutral: 'bg-[var(--text-weaker)]',
}
const VALUE: Partial<Record<Tone, string>> = {
    danger: 'text-[var(--text-danger)]',
    warning: 'text-[var(--text-warning)]',
}

export function KpiTile({ label, value, sub, tone, testId }: { label: string; value: ReactNode | undefined; sub?: ReactNode; tone?: Tone; testId?: string }) {
    return (
        <div
            data-testid={testId}
            aria-busy={value === undefined || undefined}
            className="flex min-w-0 flex-col gap-[4px] rounded-[8px] border border-solid border-[var(--border-default)] bg-[var(--surface-default)] p-[16px] max-sm:p-[12px]"
        >
            <p className="m-0 flex min-w-0 items-center gap-[6px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
                {tone && <span aria-hidden className={cx('size-[6px] shrink-0 rounded-[3px]', DOT[tone])} />}
                <span className="truncate">{label}</span>
            </p>
            <p className={cx('m-0 text-[22px] leading-[28px] font-semibold [overflow-wrap:anywhere] tabular-nums', (tone && VALUE[tone]) ?? 'text-[var(--text-default)]')}>
                {value === undefined ? <Skeleton width={72} height={22} className="my-[3px]" /> : value}
            </p>
            {sub !== undefined && <div className="text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{sub}</div>}
        </div>
    )
}
